import React, { useState, useRef, useEffect } from 'react'
import { useAuth } from '../hooks/useAuth'
import { Button } from '../components/Button'
import { PackageManagement } from '../components/admin/PackageManagement'
import { OrderManagement } from '../components/admin/OrderManagement'
import { UserManagement } from '../components/admin/UserManagement'
import { SystemStatus } from '../components/admin/SystemStatus'

type AdminTab = 'packages' | 'orders' | 'users' | 'system'

export function AdminPage() {
  const { user, isAuthenticated, isAdmin, isLoading } = useAuth()
  const [activeTab, setActiveTab] = useState<AdminTab>('packages')
  // const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)

  // Show loading state while authentication is being determined
  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading admin panel...</p>
        </div>
      </div>
    )
  }

  // Check authentication and admin status after loading is complete
  if (!isAuthenticated || !isAdmin()) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Access Denied</h1>
          <p className="text-gray-600 mb-8">
            You need administrator privileges to access this page.
          </p>
          <Button onClick={() => window.location.href = '/'}>
            Return Home
          </Button>
        </div>
      </div>
    )
  }

  const tabs = [
    { id: 'packages' as AdminTab, label: 'Package Management', icon: '📦' },
    { id: 'orders' as AdminTab, label: 'Order Management', icon: '🛒' },
    { id: 'users' as AdminTab, label: 'User Management', icon: '👥' },
    { id: 'system' as AdminTab, label: 'System Status', icon: '⚙️' },
  ]

  // Check scroll position
  const checkScrollPosition = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current
      setCanScrollLeft(scrollLeft > 0)
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth)
    }
  }

  // Handle scroll functions
  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -200, behavior: 'smooth' })
    }
  }

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 200, behavior: 'smooth' })
    }
  }

  // Touch/swipe handling
  const [touchStart, setTouchStart] = useState<number | null>(null)
  const [touchEnd, setTouchEnd] = useState<number | null>(null)

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null)
    setTouchStart(e.targetTouches[0].clientX)
  }

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX)
  }

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return

    const distance = touchStart - touchEnd
    const isLeftSwipe = distance > 50
    const isRightSwipe = distance < -50

    if (isLeftSwipe && canScrollRight) {
      scrollRight()
    }
    if (isRightSwipe && canScrollLeft) {
      scrollLeft()
    }
  }

  useEffect(() => {
    checkScrollPosition()
    const handleResize = () => checkScrollPosition()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', checkScrollPosition)
      return () => scrollContainer.removeEventListener('scroll', checkScrollPosition)
    }
  }, [])

  const renderTabContent = () => {
    switch (activeTab) {
      case 'packages':
        return <PackageManagement />
      case 'orders':
        return <OrderManagement />
      case 'users':
        return <UserManagement />
      case 'system':
        return <SystemStatus />
      default:
        return <PackageManagement />
    }
  }

  return (
    <>
      {/* Fixed Admin Tab Navigation - positioned right below header */}
      <div className="admin-tabs-sticky">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Horizontal Scrollable Tab Navigation */}
          <div className="flex items-center overflow-hidden">
            {/* Left scroll button */}
            <button
              onClick={scrollLeft}
              className={`mr-2 p-2 rounded-full transition-colors ${
                canScrollLeft
                  ? 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'
                  : 'text-gray-300 cursor-not-allowed'
              }`}
              disabled={!canScrollLeft}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            {/* Scrollable tab container */}
            <div
              ref={scrollContainerRef}
              className="flex-1 overflow-x-auto scrollbar-hide"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              <nav className="flex space-x-2 py-2 sm:space-x-4 sm:py-4">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`tab-button flex items-center space-x-2 px-4 py-2 sm:px-6 sm:py-3 rounded-lg font-medium text-sm whitespace-nowrap ${
                      activeTab === tab.id ? 'active' : ''
                    }`}
                  >
                    <span className="text-lg">{tab.icon}</span>
                    <span>{tab.label}</span>
                  </button>
                ))}
              </nav>
            </div>

            {/* Right scroll button */}
            <button
              onClick={scrollRight}
              className={`ml-2 p-2 rounded-full transition-colors ${
                canScrollRight
                  ? 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'
                  : 'text-gray-300 cursor-not-allowed'
              }`}
              disabled={!canScrollRight}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            🔧 Administration Panel
          </h1>
          <p className="text-gray-600">
            Manage packages, orders, users, and system settings for NickFixIt Shop.
          </p>
        </div>

        {/* Admin Stats Cards - Horizontally Scrollable */}
        <div className="mb-6">
          <div className="overflow-x-auto scrollbar-hide">
            <div className="flex space-x-2 pb-2 sm:space-x-4 sm:pb-4" style={{ minWidth: 'max-content' }}>
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 sm:p-4 min-w-[150px] sm:min-w-[200px] flex-shrink-0">
                <div className="flex items-center">
                  <div className="text-xl mr-2">📦</div>
                  <div>
                    <p className="text-xs font-medium text-gray-600">Total Packages</p>
                    <p className="text-lg font-bold text-gray-900">12</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 sm:p-4 min-w-[150px] sm:min-w-[200px] flex-shrink-0">
                <div className="flex items-center">
                  <div className="text-xl mr-2">🛒</div>
                  <div>
                    <p className="text-xs font-medium text-gray-600">Active Orders</p>
                    <p className="text-lg font-bold text-gray-900">8</p>
                  </div>
                </div>
              </div>

                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 sm:p-4 min-w-[150px] sm:min-w-[200px] flex-shrink-0">
                <div className="flex items-center">
                  <div className="text-xl mr-2">👥</div>
                  <div>
                    <p className="text-xs font-medium text-gray-600">Total Users</p>
                    <p className="text-lg font-bold text-gray-900">45</p>
                  </div>
                </div>
              </div>

                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 sm:p-4 min-w-[150px] sm:min-w-[200px] flex-shrink-0">
                <div className="flex items-center">
                  <div className="text-xl mr-2">💰</div>
                  <div>
                    <p className="text-xs font-medium text-gray-600">Monthly Revenue</p>
                    <p className="text-lg font-bold text-gray-900">$2,340</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tab Content */}
        <div className="min-h-96">
          {renderTabContent()}
        </div>
      </div>
    </>
  )
}